import { useSyncExternalStore } from "react";

/**
 * One shared ticker per interval: hundreds of table rows subscribe to the same
 * `setInterval`, which starts with the first subscriber and stops with the last.
 */
interface Ticker {
  now: number;
  subscribers: Set<() => void>;
  timer: ReturnType<typeof setInterval> | null;
  subscribe: (onTick: () => void) => () => void;
}

const tickers = new Map<number, Ticker>();

function tickerFor(intervalMs: number): Ticker {
  const existing = tickers.get(intervalMs);
  if (existing) return existing;
  const ticker: Ticker = {
    now: Date.now(),
    subscribers: new Set(),
    timer: null,
    subscribe: (onTick) => {
      ticker.subscribers.add(onTick);
      if (ticker.timer === null) {
        // A ticker idle since its last subscriber left would hand out a stale
        // snapshot until the first tick; refresh it as the timer restarts.
        ticker.now = Date.now();
        ticker.timer = setInterval(() => {
          ticker.now = Date.now();
          for (const notify of ticker.subscribers) notify();
        }, intervalMs);
      }
      return () => {
        ticker.subscribers.delete(onTick);
        if (ticker.subscribers.size === 0 && ticker.timer !== null) {
          clearInterval(ticker.timer);
          ticker.timer = null;
        }
      };
    },
  };
  tickers.set(intervalMs, ticker);
  return ticker;
}

/**
 * Current time (epoch ms), re-rendering every `intervalMs` — for renders that
 * compare a stored instant against "now", so time-gated UI updates without a refetch.
 */
export function useNow(intervalMs: number = 30_000): number {
  const ticker = tickerFor(intervalMs);
  return useSyncExternalStore(
    ticker.subscribe,
    () => ticker.now,
    () => ticker.now,
  );
}
